// Version check utility to detect when a new deployment is available
// This helps ensure users always see the latest version without manual refresh

const VERSION_CHECK_INTERVAL = 2 * 60 * 1000; // Check every 2 minutes
const AUTO_REFRESH_COUNTDOWN = 10 * 60; // 10 minutes in seconds
let currentVersion = null;
let checkInterval = null;
let countdownInterval = null;
let countdownSeconds = AUTO_REFRESH_COUNTDOWN;
let versionCheckStarted = false;

/**
 * Gets the version from the currently loaded scripts (embedded in page)
 */
const getEmbeddedVersion = () => {
  try {
    const scripts = document.querySelectorAll('script[src*="main."]');
    for (const script of scripts) {
      const src = script.getAttribute('src') || '';
      const match = src.match(/main\.([a-f0-9]+)\.js/i);
      if (match) {
        return match[1];
      }
    }
  } catch (e) {
    console.warn('Could not get embedded version:', e);
  }
  return null;
};

/**
 * Fetches the current version from the server by checking index.html
 * The version is determined by the hash in the main JS bundle filename
 */
const fetchVersion = async () => {
  try {
    // Add timestamp to prevent caching - use full origin URL
    const url = `${window.location.origin}/index.html?nocache=${Date.now()}&r=${Math.random()}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    
    if (!response.ok) {
      console.warn('Version check: response not ok', response.status);
      return null;
    }
    
    const html = await response.text();
    // Extract the main JS bundle filename which contains the content hash
    // Matches patterns like: main.ed64899a.js or main.abc123.js
    const match = html.match(/main\.([a-f0-9]+)\.js/i);
    if (match) {
      console.log('Version check: found version hash', match[1]);
      return match[1];
    }
    
    // Fallback: try to match any JS file with hash pattern
    const fallbackMatch = html.match(/static\/js\/[^"']+\.([a-f0-9]{8})\.js/i);
    if (fallbackMatch) {
      console.log('Version check: found fallback version hash', fallbackMatch[1]);
      return fallbackMatch[1];
    }
    
    console.warn('Version check: no version hash found in HTML');
    return null;
  } catch (error) {
    console.warn('Version check failed:', error);
    return null;
  }
};

/**
 * Starts the countdown timer for auto-refresh
 * @param {Function} onTick - Callback with remaining seconds
 * @param {Function} onExpire - Callback when countdown reaches 0
 */
export const startCountdown = (onTick, onExpire) => {
  // Reset countdown
  countdownSeconds = AUTO_REFRESH_COUNTDOWN;
  
  // Clear any existing countdown
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }
  
  // Start countdown
  countdownInterval = setInterval(() => {
    countdownSeconds--;
    
    if (onTick) {
      onTick(countdownSeconds);
    }
    
    if (countdownSeconds <= 0) {
      clearInterval(countdownInterval);
      countdownInterval = null;
      if (onExpire) {
        onExpire();
      } else {
        forceRefresh();
      }
    }
  }, 1000);
};

/**
 * Stops the countdown timer
 */
export const stopCountdown = () => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
  countdownSeconds = AUTO_REFRESH_COUNTDOWN;
};

/**
 * Gets the initial countdown duration in seconds
 */
export const getCountdownDuration = () => AUTO_REFRESH_COUNTDOWN;

/**
 * Starts periodic version checking
 * @param {Function} onNewVersion - Callback when new version is detected
 */
export const startVersionCheck = (onNewVersion) => {
  // Prevent duplicate initialization
  if (versionCheckStarted) {
    console.log('Version check already started, skipping...');
    return;
  }
  versionCheckStarted = true;
  
  console.log('Starting version check system...');
  
  // Get initial version from embedded scripts (most reliable)
  currentVersion = getEmbeddedVersion();
  console.log('App version initialized from embedded scripts:', currentVersion);
  
  if (!currentVersion) {
    // Fallback: try to fetch from index.html
    fetchVersion().then(version => {
      currentVersion = version;
      console.log('App version initialized from fetch:', version);
      if (!version) {
        console.warn('Could not determine initial app version - version check may not work');
      }
    });
  }

  // Periodically check for updates
  checkInterval = setInterval(async () => {
    console.log('Checking for new version...');
    const latestVersion = await fetchVersion();
    
    console.log('Version comparison - Current:', currentVersion, 'Latest:', latestVersion);
    
    if (latestVersion && currentVersion && latestVersion !== currentVersion) {
      console.log('🚀 New version detected:', latestVersion, 'Current:', currentVersion);
      clearInterval(checkInterval); // Stop checking once update is detected
      if (onNewVersion) {
        onNewVersion();
      }
    }
  }, VERSION_CHECK_INTERVAL);

  // Also check when user returns to the tab (visibility change)
  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState === 'visible' && currentVersion) {
      console.log('Tab focused - checking for updates...');
      const latestVersion = await fetchVersion();
      
      if (latestVersion && currentVersion && latestVersion !== currentVersion) {
        console.log('🚀 New version detected on tab focus:', latestVersion);
        clearInterval(checkInterval); // Stop checking once update is detected
        if (onNewVersion) {
          onNewVersion();
        }
      }
    }
  });
};

/**
 * Stops version checking
 */
export const stopVersionCheck = () => {
  if (checkInterval) {
    clearInterval(checkInterval);
    checkInterval = null;
  }
};

/**
 * Forces a hard refresh to get the latest version
 */
export const forceRefresh = () => {
  // Clear all caches if available
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => caches.delete(name));
    });
  }
  
  // Force reload from server
  window.location.reload(true);
};
